/**
 * Profile Service
 * Handles user profile management operations
 */

import type { GatewayClient } from './client'

// ============================================================================
// Types
// ============================================================================

export interface UserProfile {
  id: string
  email: string
  displayName: string
  firstName?: string
  lastName?: string
  avatarUrl?: string
  roles: string[]
  linkedAccountId?: string
  profileCustomized: boolean
  hasPassword: boolean
  createdAt: string
  lastLoginAt?: string
}

export interface UpdateNameRequest {
  firstName: string
  lastName: string
  displayName?: string
}

export interface InitiateEmailChangeRequest {
  newEmail: string
  password: string
}

export interface VerifyEmailChangeRequest {
  token: string
}

export interface SetPasswordRequest {
  currentPassword?: string
  newPassword: string
}

export interface PasswordStatusResponse {
  hasPassword: boolean
}

export interface AvatarResponse {
  avatarUrl: string
}

export interface MessageResponse {
  message: string
}

export interface LinkedIdentity {
  provider: string
  providerUserId: string
  email?: string
  displayName?: string
  avatarUrl?: string
  createdAt: string
  lastUsedAt: string
}

export interface LinkedIdentitiesResponse {
  identities: LinkedIdentity[]
}

// ============================================================================
// Service Interface
// ============================================================================

export interface ProfileService {
  /**
   * Get current user profile
   */
  getProfile(): Promise<UserProfile>

  /**
   * Update user name (first name, last name, optionally display name)
   */
  updateName(req: UpdateNameRequest): Promise<UserProfile>

  /**
   * Initiate email change - sends verification to new email
   */
  initiateEmailChange(req: InitiateEmailChangeRequest): Promise<MessageResponse>

  /**
   * Verify email change with token
   */
  verifyEmailChange(req: VerifyEmailChangeRequest): Promise<UserProfile>

  /**
   * Get password status (has password or not)
   */
  getPasswordStatus(): Promise<PasswordStatusResponse>

  /**
   * Set or change password
   */
  setPassword(req: SetPasswordRequest): Promise<MessageResponse>

  /**
   * Upload avatar image
   */
  uploadAvatar(file: File): Promise<AvatarResponse>

  /**
   * Delete avatar
   */
  deleteAvatar(): Promise<void>

  /**
   * Get linked OAuth identities
   */
  getLinkedIdentities(): Promise<LinkedIdentitiesResponse>

  /**
   * Unlink an OAuth identity
   */
  unlinkIdentity(provider: string): Promise<void>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create a ProfileService instance
 */
export function createProfileService(client: GatewayClient): ProfileService {
  return {
    async getProfile(): Promise<UserProfile> {
      return client.get<UserProfile>('/v1/user/me')
    },

    async updateName(req: UpdateNameRequest): Promise<UserProfile> {
      return client.patch<UserProfile>('/v1/user/me/name', {
        firstName: req.firstName,
        lastName: req.lastName,
        displayName: req.displayName,
      })
    },

    async initiateEmailChange(
      req: InitiateEmailChangeRequest
    ): Promise<MessageResponse> {
      return client.post<MessageResponse>('/v1/user/me/email/change', {
        newEmail: req.newEmail,
        password: req.password,
      })
    },

    async verifyEmailChange(
      req: VerifyEmailChangeRequest
    ): Promise<UserProfile> {
      return client.post<UserProfile>('/v1/user/me/email/verify', {
        token: req.token,
      })
    },

    async getPasswordStatus(): Promise<PasswordStatusResponse> {
      return client.get<PasswordStatusResponse>('/v1/user/me/password/status')
    },

    async setPassword(req: SetPasswordRequest): Promise<MessageResponse> {
      return client.post<MessageResponse>('/v1/user/me/password', {
        currentPassword: req.currentPassword,
        newPassword: req.newPassword,
      })
    },

    async uploadAvatar(file: File): Promise<AvatarResponse> {
      const response = await client.uploadFile('/v1/user/me/avatar', file, {})
      if (!response.ok) {
        throw new Error(`Failed to upload avatar: ${response.statusText}`)
      }
      return response.json() as Promise<AvatarResponse>
    },

    async deleteAvatar(): Promise<void> {
      await client.delete('/v1/user/me/avatar')
    },

    async getLinkedIdentities(): Promise<LinkedIdentitiesResponse> {
      return client.get<LinkedIdentitiesResponse>('/v1/user/me/identities')
    },

    async unlinkIdentity(provider: string): Promise<void> {
      await client.delete(`/v1/user/me/identities/${provider}`)
    },
  }
}
